import { useState, useEffect } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import advertisementService from '../../services/advertisementService';
import Loading from '../../components/Common/Loading';
import Button from '../../components/Common/Button';
import Table from '../../components/Common/Table';
import CreateAdvertisementModal from './CreateAdvertisementModal';

const AdvertisementsIndex = () => {
  const { get, isRtl } = useLocalization();
  const [advertisements, setAdvertisements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [adToEdit, setAdToEdit] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadAdvertisements();
  }, []);

  const loadAdvertisements = async () => {
    try {
      setIsLoading(true);
      const data = await advertisementService.getAll();
      setAdvertisements(data);
    } catch (error) {
      console.error('Error loading advertisements:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async (id) => {
    try {
      setLoadingDetails(true);
      const details = await advertisementService.getDetails(id);
      setAdToEdit(details);
      setShowCreateModal(true);
    } catch (error) {
      console.error('Error loading advertisement details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setAdToEdit(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm(isRtl ? 'هل أنت متأكد من حذف هذا الإعلان؟' : 'Are you sure you want to delete this advertisement?')) {
      try {
        await advertisementService.delete(id);
        loadAdvertisements();
      } catch (error) {
        console.error('Error deleting advertisement:', error);
      }
    }
  };

  const filteredAds = advertisements.filter(ad => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      ad.titleEn?.toLowerCase().includes(search) ||
      ad.titleAr?.toLowerCase().includes(search)
    );
  });

  const columns = [
    {
      header: isRtl ? 'معاينة' : 'Preview',
      accessor: 'imageUrl',
      render: (value, row) => {
        const src = row?.imageUrlFull || value;
        return src ? (
          <img
            src={src}
            alt="Advertisement"
            style={{ width: '60px', height: '40px', borderRadius: 'var(--r-md)', objectFit: 'cover' }}
          />
        ) : '-';
      },
    },
    { header: isRtl ? 'العنوان' : 'Title', accessor: 'titleEn' },
    {
      header: isRtl ? 'المواضع' : 'Placements',
      accessor: 'placementCount',
      render: (value) => (value != null ? (isRtl ? `${value} مواضع` : `${value} placement(s)`) : '-'),
    },
    {
      header: isRtl ? 'الحالة' : 'Status',
      accessor: 'isActive',
      render: (value) => (
        <span className={`badge ${value ? 'badge-success' : 'badge-neutral'}`}>
          {value ? (isRtl ? 'نشط' : 'Active') : (isRtl ? 'غير نشط' : 'Inactive')}
        </span>
      ),
    },
    {
      header: isRtl ? 'الإجراءات' : 'Actions',
      accessor: 'id',
      render: (value) => (
        <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
          <Button
            size="small"
            variant="secondary"
            onClick={() => handleEdit(value)}
            disabled={loadingDetails}
          >
            {isRtl ? 'تعديل' : 'Edit'}
          </Button>
          <Button size="small" variant="danger" onClick={() => handleDelete(value)}>
            {get('delete')}
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <Loading text="Loading advertisements..." />;
  }

  return (
    <div className="advertisements-content">
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-main">
            <h1 className="page-title">{isRtl ? 'إدارة الإعلانات' : 'Advertisement Management'}</h1>
            <p className="page-description">{isRtl ? 'إدارة اللافتات الترويجية والمواضع' : 'Manage promotional banners and placements'}</p>
          </div>
          <Button variant="primary" onClick={() => { setAdToEdit(null); setShowCreateModal(true); }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
            </svg>
            {get('add_advertisement')}
          </Button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">{isRtl ? 'جميع الإعلانات' : 'All Advertisements'}</h3>
          <div className="search-box" style={{ maxWidth: '300px' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder={isRtl ? 'بحث...' : 'Search...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="card-body">
          <Table
            columns={columns}
            data={filteredAds}
            emptyMessage={isRtl ? 'لا توجد إعلانات' : 'No advertisements found'}
          />
        </div>
      </div>

      {showCreateModal && (
        <CreateAdvertisementModal
          isOpen={showCreateModal}
          onClose={handleCloseModal}
          onSuccess={() => {
            handleCloseModal();
            loadAdvertisements();
          }}
          adToEdit={adToEdit}
        />
      )}
    </div>
  );
};

export default AdvertisementsIndex;
